import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Api from "../utils/Api";
import Swal from "sweetalert2";
import { useDebounce } from "use-debounce";
import { useSelector } from "react-redux";

const eventContext = createContext(null);

export default function EventProvider({ children }) {
    const { user } = useSelector((state) => state.user);

    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [searchText, setSearchText] = useState("");
    const [eventType, setEventType] = useState("");
    const [program, setProgram] = useState("");

    const [debouncedSearchText] = useDebounce(searchText, 500);

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        // onSearchChange(e.target.value); // Pass value to parent
    };

    const handleFilterChange = (e) => {
        if (e.target.name === "eventType") setEventType(e.target.value);
        if (e.target.name === "program") setProgram(e.target.value);
        // onFilterChange(e.target.name, e.target.value); // Pass value to parent
    };

    const API_URL = useMemo(() => {
        let url = `/events?page=${page}&limit=all`;

        if (debouncedSearchText) {
            url += `&searchText=${encodeURIComponent(debouncedSearchText)}`;
        }

        if (eventType) {
            url += `&eventType=${encodeURIComponent(eventType)}`;
        }

        if (program) {
            url += `&program=${encodeURIComponent(program)}`;
        }
        if (user && user?.role !== 'super admin') {
            url += `&uploded_by=${user.loggedinUserId}`
        }

        return url;
    }, [page, debouncedSearchText, eventType, program, user]);

    const fetchEvents = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await Api.get(API_URL);
            setEvents(response.data.data);
            setTotalCount(response.data.pagination.totalCount);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error("Failed to fetch events", error);
        } finally {
            setIsLoading(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchEvents();
    }, [API_URL, page, fetchEvents]);

    // Handle delete functionality
    const handleDeleteEvent = async (id) => {
        try {
            Swal.fire({
                // title: 'Are you sure?',
                title: 'Bist du sicher?',
                // text: `You are about to delete a event. This action cannot be undone.`,
                text: `Sie sind dabei, ein Ereignis zu löschen. Diese Aktion kann nicht rückgängig gemacht werden.`,
                showCancelButton: true,
                cancelButtonText: 'Abbrechen',
                confirmButtonText: `Ja, löschen`,
                confirmButtonColor: '#d55',
                cancelButtonColor: '#000',
                reverseButtons: true,
                background: '#f2f2f2',
                color: '#000',
                customClass: {
                    title: 'swal-title',
                    confirmButton: 'swal-confirm-button',
                    cancelButton: 'swal-cancel-button',
                },
            }).then(async result => {
                if (result.isConfirmed) {
                    try {
                        setIsLoading(true);
                        await Api.delete(`/delete-event/${id}`);
                        setEvents(events.filter(event => event.id !== id));
                        Swal.fire({
                            icon: "success",
                            title: "Erfolg",
                            // text: "Listing deleted successfully",
                            text: "Ereignis erfolgreich gelöscht",
                            iconColor: '#000',
                            confirmButtonColor: '#000'
                        });
                    } catch (error) {
                        console.error("Failed to delete event", error);
                        alert('Eintrag konnte nicht gelöscht werden');
                    } finally {
                        setIsLoading(false);
                    }
                }
            });
        } catch (error) {
            console.error("Failed to update status", error.message);
        }
    };

    return (
        <eventContext.Provider value={{
            events,
            handleDeleteEvent,
            isLoading,
            page,
            setPage,
            totalCount,
            totalPages,
            fetchEvents,
            searchText,
            eventType,
            program,
            handleSearchChange,
            handleFilterChange
        }}>
            {children}
        </eventContext.Provider>
    )
}

export const useEvent = () => useContext(eventContext);